import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { formatFxRate } from "@/pages/contracts/fxRate";

interface Props {
  rate: number | null | undefined;
  contractCurrency: string | null;
  // Null until the account-currency setting resolves.
  accountCurrency: string | null;
  // Set for the current rate when someone typed it in by hand.
  isManual?: boolean;
}

// One FX rate in the contracts table, read as `1 AUD = 0.7030 USD`. A row in
// the account's own currency has no rate, and shows the dash.
const FxRateCell: React.FC<Props> = ({
  rate,
  contractCurrency,
  accountCurrency,
  isManual = false
}) => {
  const { t } = useTranslation();
  if (rate == null || !contractCurrency || !accountCurrency) {
    return <Text variant="body">—</Text>;
  }
  return (
    <div className="flex items-center gap-1 min-w-0">
      <Text variant="body" className="truncate">
        {formatFxRate(rate, contractCurrency, accountCurrency)}
      </Text>
      {isManual && (
        // Who set it and when is on the contract page; the table only says
        // that the rate no longer follows the automatic refresh.
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} data-test="fx-rate-cell-manual">
              <Badge
                text={t("contracts.fxRate.manual")}
                textVariant="smBody"
                className="py-0.5"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent enableMarkdown={false}>
            {t("contracts.fxRate.manualTooltip")}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export default FxRateCell;
